import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { SalesItemRecord, TireClassification, TireQuoteItemRecordCustomer } from '@navio/nsdk';
import { cn, NoddiIcon } from 'noddi-ui-common';
import { NoddiBadge, NoddiButton, NoddiNumberStepper } from 'noddi-ui-web';
import { formatCurrencyAmount, getDimensionsLoadSpeedString, getTireBrandName, getTirePrice } from 'noddi-util';
import { GenericItemPriceLines, getBestTireToDisplay, TireEuLabelScale } from 'noddi-web';
import { useState } from 'react';
import { getCommonTranslations } from '../../../utils/commonTranslations';
import { TireOfferErrorComponent } from '../TireOfferErrorComponent';
import { getTireOfferTotal } from '../tireOfferTotal';
import { TireOptionInfoSheet } from './TireOptionInfoSheet';
import { getTireOptionQuantityRows, withTireOptionQuantities } from './tireOptionQuantities';

type Props = {
  quoteItem: TireQuoteItemRecordCustomer;
  position: number;
  selected: boolean;
  /** Picks this option with the quantities on the card; a quantity change picks it too. */
  onPick: (quoteItem: TireQuoteItemRecordCustomer) => void;
  genericItems: SalesItemRecord[];
  /** Tire hotel tires are fitted on the stored wheel set at our facility, not delivered to the customer's home. */
  isTireHotel: boolean;
  slug: string;
};

/**
 * One tire option of the offer: the tire, its EU label, a quantity per axle and what it all costs.
 * The tire row is the radio; it stretches over the card so a tap anywhere picks it, and the controls
 * sit above that stretch.
 */
export const TireOptionCard = ({ quoteItem, position, selected, onPick, genericItems, isTireHotel, slug }: Props) => {
  const rows = getTireOptionQuantityRows(quoteItem);
  const [quantities, setQuantities] = useState(() => rows?.map((row) => row.initial) ?? []);
  const [infoOpen, setInfoOpen] = useState(false);

  if (!rows) {
    return (
      <TireOfferErrorComponent
        title={t`Tire not found`}
        description={t`Got a tire option without its tires - quote with slug ${slug}`}
      />
    );
  }

  const [{ tire: headlineTire }] = rows;
  const tire = headlineTire.tireInventory.tire;
  const imageUrl = headlineTire.tireInventory.imageTire?.url ?? headlineTire.tireInventory.imageUrl ?? undefined;
  const brandName = getTireBrandName(tire);
  const classification = getClassificationLabel(tire.classification);
  const texts = getCommonTranslations().tireSelection;
  // A staggered option shows the label of whichever axle's tire grades better, as the shared cards do.
  const euLabelTire = rows[1]
    ? getBestTireToDisplay({
        frontTireFromInventorySupplier: headlineTire.tireInventory,
        rearTireFromInventorySupplier: rows[1].tire.tireInventory
      }).tire
    : tire;
  const picked = withTireOptionQuantities(quoteItem, quantities);
  const total = formatCurrencyAmount({
    money: { amount: getTireOfferTotal(picked.quoteItemTires, genericItems) },
    decimals: 0
  });

  const changeQuantity = (index: number, value: number) => {
    const next = quantities.map((quantity, i) => (i === index ? value : quantity));
    setQuantities(next);
    onPick(withTireOptionQuantities(quoteItem, next));
  };

  return (
    <div
      className={cn(
        'relative flex flex-col gap-5 rounded-xl bg-card p-5',
        selected ? 'border-2 border-primary ring-4 ring-primary/15' : 'border border-border shadow-xs'
      )}
    >
      <div className='flex items-center justify-between gap-2'>
        <div className='flex items-center gap-2'>
          <span className='text-sm font-semibold text-muted-foreground'>{t`Alt. ${position}`}</span>
          {classification && <NoddiBadge variant='outline'>{classification}</NoddiBadge>}
        </div>
        {quoteItem.isRecommended && (
          <NoddiBadge>
            <NoddiIcon name='Star' size='xSmall' />
            <Trans>We recommend</Trans>
          </NoddiBadge>
        )}
      </div>

      <button
        type='button'
        role='radio'
        aria-checked={selected}
        onClick={() => onPick(picked)}
        className='flex items-center gap-4 text-left outline-hidden after:absolute after:inset-0 after:rounded-xl focus-visible:after:ring-3 focus-visible:after:ring-primary/40'
      >
        {imageUrl && <img src={imageUrl} alt='' className='h-20 w-auto flex-none object-contain' />}
        <span className='flex min-w-0 flex-1 flex-col'>
          <span className='text-sm font-medium text-muted-foreground'>{brandName}</span>
          <span className='text-lg leading-tight font-semibold'>{tire.model}</span>
        </span>
        <span
          className={cn(
            'flex size-6 flex-none items-center justify-center rounded-full border-2',
            selected ? 'border-primary bg-primary' : 'border-border bg-card'
          )}
        >
          {selected && <span className='size-2.5 rounded-full bg-primary-foreground' />}
        </span>
      </button>

      <TireEuLabelScale tire={euLabelTire} translations={texts} className='rounded-lg bg-muted/60 p-4' />

      <div className='relative flex flex-col gap-3'>
        {rows.map((row, index) => {
          const rowLabel = row.label;
          return (
            <div key={row.tire.id} className='flex items-center gap-3'>
              <div className='flex min-w-0 flex-1 flex-col'>
                <span className='text-sm font-semibold'>{row.label}</span>
                <span className='text-xs text-muted-foreground'>
                  {getDimensionsLoadSpeedString(row.tire.tireInventory.tire)}
                </span>
              </div>
              <NoddiNumberStepper
                value={quantities[index] ?? row.initial}
                onChange={(value) => changeQuantity(index, value)}
                min={row.min}
                max={row.max}
                formatValue={(value) => `${value} ${texts.pcs}`}
                decrementLabel={t`${rowLabel}: one fewer`}
                incrementLabel={t`${rowLabel}: one more`}
              />
            </div>
          );
        })}
      </div>

      <div className='flex flex-col gap-1.5 border-t border-border pt-4 text-sm'>
        {rows.map((row, index) => {
          const quantity = quantities[index] ?? 0;
          const price = getTirePrice(row.tire);
          return (
            <div key={row.tire.id} className='flex justify-between gap-4'>
              <span>
                {row.label}, {quantity} {texts.pcs}
              </span>
              <span>
                {formatCurrencyAmount({
                  money: { amount: price.amount * quantity, currency: price.currency },
                  decimals: 0
                })}
              </span>
            </div>
          );
        })}
        <GenericItemPriceLines genericItems={genericItems} />
        <div className='flex justify-between gap-4'>
          <span>{isTireHotel ? t`Delivery` : t`Delivery to your home`}</span>
          <span className='font-semibold text-successDark'>{t`Free`}</span>
        </div>
        <div className='flex items-baseline justify-between gap-4 pt-1.5 text-lg font-bold'>
          <span>{texts.total}</span>
          <span>{total}</span>
        </div>
      </div>

      <NoddiButton variant='outline' className='relative w-full' onClick={() => setInfoOpen(true)}>
        <Trans>More about the tire</Trans>
      </NoddiButton>

      <TireOptionInfoSheet
        open={infoOpen}
        onOpenChange={setInfoOpen}
        rows={rows}
        classification={classification}
        total={total}
        onChoose={() => {
          onPick(picked);
          setInfoOpen(false);
        }}
      />
    </div>
  );
};

const getClassificationLabel = (classification: string | null | undefined) => {
  switch (classification) {
    case TireClassification.PREMIUM:
      return t`Premium`;
    case TireClassification.QUALITY:
      return t`Standard`;
    case TireClassification.ECONOMY:
    case TireClassification.BUDGET:
      return t`Economy`;
    default:
      return undefined;
  }
};
